// The cross-widget event link, ported from window.EventMapKit (js/renderers-shotmap.js)
// — Pass Map ⇄ Passing Profile today, designed so Shot Map / Heat Map can join later.
//
// Two plain window-level CustomEvents, matched by each pass's globally-unique StatsBomb
// id (so a broadcast a module doesn't own — wrong player, or no match — is a harmless
// no-op). Kept as TWO event types on purpose: a hover reverts the moment the cursor
// leaves, a wedge's group selection persists until cleared, and one must never wipe the
// other.
//
// The event NAMES + detail shapes are the contract, and must stay byte-identical to the
// legacy's: during the port a React module and a legacy one talk through the same events.
//
// React note: the legacy re-anchored one listener per render on the module element
// (`anchorEl._eventHighlightOff`) and leaked it when a module closed. Here subscribe
// returns an unsubscribe, for a useEffect cleanup — no stacking, no leak.
//
// `target` defaults to window; tests pass their own EventTarget (no jsdom in this repo).

export const EVENT_HIGHLIGHT_TYPE = "catalyst:event-highlight";
export const EVENT_SELECTION_TYPE = "catalyst:event-selection";

/** One hovered pass id, or null when the hover ends. */
export function broadcastEventHighlight(id: string | null, target: EventTarget = window): void {
  target.dispatchEvent(new CustomEvent(EVENT_HIGHLIGHT_TYPE, { detail: { id } }));
}

export function subscribeEventHighlight(handler: (id: string | null) => void, target: EventTarget = window): () => void {
  const listener = (e: Event) => handler((e as CustomEvent<{ id: string | null }>).detail.id);
  target.addEventListener(EVENT_HIGHLIGHT_TYPE, listener);
  return () => target.removeEventListener(EVENT_HIGHLIGHT_TYPE, listener);
}

/**
 * The shared pass selection (Ben, 2026-10-21: "the three passing modules need to talk to each other
 * and reflect each other's selections"). Each of the Passing Report, Pass Map and Passing Profile holds
 * at most one pick (a Report bar / band / card, a Profile wedge, Pass Map zones or a Summary row, plus
 * its filter chips). Picks COMBINE (Ben, 2026-10-26: "filtered, not replaced"): a module shows the
 * passes in every OTHER module's pick, one blue chip each, and keeps its own pick's highlight. A new
 * pick replaces only the same module's earlier one. Kept here so a module that re-draws (or opens)
 * picks them up again.
 */
export interface PassSelection {
  ids: string[];
  /** "Hard passes", "Passes from 2 zones" — the chip's words. */
  label: string;
  /** The module element it was picked in. */
  source: HTMLElement;
}
const selections = new Map<HTMLElement, PassSelection>();

/** Every module's current pick, oldest first. A closed module's pick is dropped. */
export function currentPassSelections(): PassSelection[] {
  for (const source of [...selections.keys()]) if (source.isConnected === false) selections.delete(source);
  return [...selections.values()];
}

/** Set `sel.source`'s pick, replacing that module's earlier one. An empty set is a release. */
export function selectPasses(sel: PassSelection, target: EventTarget = window): void {
  if (sel.ids.length) selections.set(sel.source, sel);
  else selections.delete(sel.source);
  target.dispatchEvent(new CustomEvent(EVENT_SELECTION_TYPE, { detail: currentPassSelections() }));
}

/** Drop `source`'s pick (it let go of it, or a blue chip's ✕). Others' picks stay. */
export function releasePasses(source: HTMLElement, target: EventTarget = window): void {
  if (!selections.delete(source)) return;
  target.dispatchEvent(new CustomEvent(EVENT_SELECTION_TYPE, { detail: currentPassSelections() }));
}

export function subscribePassSelection(handler: (sels: PassSelection[]) => void, target: EventTarget = window): () => void {
  const listener = (e: Event) => handler((e as CustomEvent<PassSelection[]>).detail);
  target.addEventListener(EVENT_SELECTION_TYPE, listener);
  return () => target.removeEventListener(EVENT_SELECTION_TYPE, listener);
}

// A pass clicked (Ben, 2026-10-20: "when I click a pass on the passing profile, it doesn't become
// selected on the pass map"): it stays highlighted, with its details, on every open Pass Map and
// Passing Profile until it's clicked again or another click clears it. One lock at a time, kept
// here so a widget that re-draws (or opens) picks it up again.
export const EVENT_LOCK_TYPE = "catalyst:event-lock";
let lockedEventId: string | null = null;

export function lockedEvent(): string | null {
  return lockedEventId;
}

export function broadcastEventLock(id: string | null, target: EventTarget = window): void {
  lockedEventId = id;
  target.dispatchEvent(new CustomEvent(EVENT_LOCK_TYPE, { detail: { id } }));
}

export function subscribeEventLock(handler: (id: string | null) => void, target: EventTarget = window): () => void {
  const listener = (e: Event) => handler((e as CustomEvent<{ id: string | null }>).detail.id);
  target.addEventListener(EVENT_LOCK_TYPE, listener);
  return () => target.removeEventListener(EVENT_LOCK_TYPE, listener);
}
